import {
  deleteGovernedLibraryDocument,
  fetchGovernedLibraryRemovalStatus,
} from "../api/cognitoryLibraryDeleteClient";
import {
  GOVERNED_LIBRARY_DELETE_FAILURE_MESSAGE,
  GOVERNED_LIBRARY_FINAL_DOCUMENT_MESSAGE,
  pollRemovalUntilTerminal,
  type CognitoryLibraryDeleteFlowPhase,
} from "./cognitoryLibraryDeletePolling";

export type { CognitoryLibraryDeleteFlowPhase } from "./cognitoryLibraryDeletePolling";

export {
  classifyRemovalStatusPoll,
  GOVERNED_LIBRARY_DELETE_FAILURE_MESSAGE,
  GOVERNED_LIBRARY_DELETE_STILL_PROCESSING_MESSAGE,
  GOVERNED_LIBRARY_FINAL_DOCUMENT_MESSAGE,
  isTerminalRemovalUserVisiblePhase,
  pollRemovalUntilTerminal,
  REMOVAL_POLL_INTERVAL_MS,
  REMOVAL_POLL_MAX_ATTEMPTS,
} from "./cognitoryLibraryDeletePolling";

export type CognitoryLibraryDeleteDialogProps = {
  cognitoryId: string;
  documentId: string;
  documentTitle: string;
  onClose: () => void;
  onDeleted: () => void;
};

export type GovernedLibraryDeleteFlowResult =
  | { kind: "deleted" }
  | { kind: "removed" }
  | { kind: "blocked_final_library_document"; message: string }
  | { kind: "still_processing"; message: string }
  | { kind: "aborted" }
  | { kind: "error"; message: string };

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => {
    window.setTimeout(resolve, ms);
  });
}

function isTerminalDeleteSuccess(
  result: GovernedLibraryDeleteFlowResult,
): boolean {
  return result.kind === "deleted" || result.kind === "removed";
}

export { isTerminalDeleteSuccess };

export async function runGovernedLibraryDeleteFlow(input: {
  cognitoryId: string;
  documentId: string;
  documentTitle: string;
  confirmed: boolean;
  signal?: AbortSignal;
  onPhaseChange?: (phase: CognitoryLibraryDeleteFlowPhase) => void;
}): Promise<GovernedLibraryDeleteFlowResult> {
  if (!input.confirmed) {
    return { kind: "error", message: "Deletion not confirmed." };
  }

  const dryRun = await deleteGovernedLibraryDocument({
    cognitoryId: input.cognitoryId,
    documentId: input.documentId,
    mode: "dry_run",
  });
  if (dryRun.kind !== "plan" && dryRun.kind !== "active_serving_removal_plan") {
    if (dryRun.kind === "blocked_final_library_document") {
      return {
        kind: "blocked_final_library_document",
        message: GOVERNED_LIBRARY_FINAL_DOCUMENT_MESSAGE,
      };
    }
    return {
      kind: "error",
      message: "Unable to prepare deletion plan.",
    };
  }

  input.onPhaseChange?.("deleting");

  const execute = await deleteGovernedLibraryDocument({
    cognitoryId: input.cognitoryId,
    documentId: input.documentId,
    mode: "execute",
    confirmDocumentId: input.documentId,
    confirmDestructiveDeletion: true,
  });

  if (execute.kind === "deleted" || execute.kind === "already_deleted") {
    return { kind: "deleted" };
  }

  if (
    execute.kind === "removal_started" ||
    execute.kind === "removal_already_pending"
  ) {
    if (execute.userVisiblePhase === "rebuilding_library") {
      input.onPhaseChange?.("rebuilding_library");
    }

    const polled = await pollRemovalUntilTerminal({
      cognitoryId: input.cognitoryId,
      documentId: input.documentId,
      fetchStatus: fetchGovernedLibraryRemovalStatus,
      sleep,
      ...(input.signal ? { signal: input.signal } : {}),
      ...(input.onPhaseChange ? { onPhaseChange: input.onPhaseChange } : {}),
    });

    if (polled.kind === "removed") {
      return { kind: "removed" };
    }
    if (polled.kind === "failed") {
      input.onPhaseChange?.("failed");
      return { kind: "error", message: polled.message };
    }
    if (polled.kind === "still_processing") {
      return { kind: "still_processing", message: polled.message };
    }
    return { kind: "aborted" };
  }

  if (execute.kind === "blocked_final_library_document") {
    return {
      kind: "blocked_final_library_document",
      message: GOVERNED_LIBRARY_FINAL_DOCUMENT_MESSAGE,
    };
  }

  input.onPhaseChange?.("failed");
  return {
    kind: "error",
    message: GOVERNED_LIBRARY_DELETE_FAILURE_MESSAGE,
  };
}
